import React, { useState, useRef, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import type { Swiper as SwiperType } from 'swiper';
import type { GalleryMedia, ImageModalProps } from '../types';
import { useTelegramWebApp } from '../../../hooks/useTelegramWebApp';
import { usePinchZoom } from './hooks/usePinchZoom';
import { usePullGestures } from './hooks/usePullGestures';
import { useDesktopZoom } from './hooks/useDesktopZoom';
import { useVideoPlayer } from './hooks/useVideoPlayer';
import { MobileCarousel } from './components/MobileCarousel';
import { DesktopView } from './components/DesktopView';
import { DesktopSidebar } from './components/DesktopSidebar';
import { MobileControls } from './components/MobileControls';
import styles from './ImageModal.module.css';

import 'swiper/swiper.css';

export const ImageModal: React.FC<ImageModalProps> = ({
  image,
  images,
  onClose,
  onNext,
  onPrev,
  onSelect,
  onDelete,
  onDownload,
  onRegenerate,
  onUse,
  onAnimate,
  onUpscale,
  onEditImage,
  onUseVideo,
  onUseReference,
  hasNext,
  hasPrev,
  onLoadMore,
  hasMoreItems,
  hideDownload,
  hideDelete,
  hideParameters,
  primaryAction,
}) => {
  const { haptic } = useTelegramWebApp();
  const [isZoomEnabled, setIsZoomEnabled] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [showActionPanel, setShowActionPanel] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const confirmDeleteTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const imageWrapperRef = useRef<HTMLDivElement>(null);
  const thumbnailsRef = useRef<HTMLDivElement>(null);
  const mobileThumbnailsRef = useRef<HTMLDivElement>(null);
  const prevImageId = useRef(image?.id);
  const isProgrammaticSlide = useRef(false);
  const pinchContainerRef = useRef<HTMLDivElement>(null);
  const actionPanelRef = useRef<HTMLDivElement>(null);
  const mobileCarouselRef = useRef<HTMLDivElement | null>(null);

  const isVideo = image?.type === 'video';
  const isSvg = !isVideo && /\.svg(\?|$)/i.test(image?.url || '');
  const currentIndex = image ? images.findIndex(img => img.id === image.id) : -1;

  const isMobileView = typeof window !== 'undefined' && window.innerWidth < 768;

  // Tools providers where "Повторить" (regenerate) makes no sense
  const TOOLS_PROVIDERS = ['video-restyle', 'video-restyle-preview', 'video-edit', 'motion-control'];
  const isToolsVideo = isVideo && image?.provider ? TOOLS_PROVIDERS.includes(image.provider) : false;
  // Upscale outputs are deterministic transforms of a source image/video — regeneration isn't meaningful.
  const canRegenerate = !!onRegenerate && !isToolsVideo && !image?.isUpscaled;

  // --- Hooks ---
  const pinchEnabled = isMobileView && !isVideo;
  const { pinchHandlers, resetZoom } = usePinchZoom(pinchContainerRef, pinchEnabled);

  const { pullRef, animateOpen, animateClose } = usePullGestures(
    onClose, setShowActionPanel, isMobileView, showActionPanel, actionPanelRef, mobileCarouselRef, pinchContainerRef,
  );

  const { handleMouseMove, handleMouseLeave, handleImageClick } = useDesktopZoom(
    imageWrapperRef, isZoomEnabled, setIsZoomEnabled,
  );

  const videoPlayer = useVideoPlayer(image?.id, image?.type);

  // --- Effects ---

  // Sync thumbnails and Swiper on image change
  useEffect(() => {
    if (!image) return;

    if (thumbnailsRef.current && currentIndex >= 0) {
      const activeThumb = thumbnailsRef.current.children[currentIndex] as HTMLElement;
      if (activeThumb) activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }

    if (mobileThumbnailsRef.current && currentIndex >= 0) {
      const activeThumb = mobileThumbnailsRef.current.children[currentIndex] as HTMLElement;
      if (activeThumb) activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }

    const isMobile = window.innerWidth < 768;
    if (isMobile && videoPlayer.swiperRef.current && currentIndex >= 0 && prevImageId.current !== image.id) {
      isProgrammaticSlide.current = true;
      videoPlayer.swiperRef.current.slideTo(currentIndex, 0);
      requestAnimationFrame(() => { isProgrammaticSlide.current = false; });
    }

    prevImageId.current = image.id;
    setConfirmingDelete(false);
    if (confirmDeleteTimer.current) clearTimeout(confirmDeleteTimer.current);
  }, [image, currentIndex, videoPlayer.swiperRef]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && hasPrev && onPrev) onPrev();
      else if (e.key === 'ArrowRight' && hasNext && onNext) onNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [hasNext, hasPrev, onNext, onPrev, onClose]);

  // Desktop thumbnail pagination
  useEffect(() => {
    const strip = thumbnailsRef.current;
    if (!strip || !onLoadMore || !hasMoreItems) return;
    const handleScroll = () => {
      if (strip.scrollLeft + strip.clientWidth >= strip.scrollWidth - 200) onLoadMore();
    };
    strip.addEventListener('scroll', handleScroll, { passive: true });
    return () => strip.removeEventListener('scroll', handleScroll);
  }, [onLoadMore, hasMoreItems]);

  // --- Handlers ---

  const handleDownload = useCallback(async (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (isDownloading || !image) return;
    haptic.impact('medium');
    setIsDownloading(true);
    try {
      if (onDownload) await onDownload(image);
      haptic.notification('success');
    } catch (error) {
      console.error('Download failed:', error);
      haptic.notification('error');
    } finally {
      setTimeout(() => setIsDownloading(false), 500);
    }
  }, [image, isDownloading, onDownload, haptic]);

  const handleDelete = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    // Block deletion of non-own items in team mode
    if (image?.isOwn === false) return;
    if (!confirmingDelete) {
      haptic.impact('light');
      setConfirmingDelete(true);
      if (confirmDeleteTimer.current) clearTimeout(confirmDeleteTimer.current);
      confirmDeleteTimer.current = setTimeout(() => setConfirmingDelete(false), 3000);
      return;
    }
    haptic.impact('medium');
    setConfirmingDelete(false);
    if (confirmDeleteTimer.current) clearTimeout(confirmDeleteTimer.current);
    if (image && onDelete) onDelete(image.id);
  }, [image, onDelete, haptic, confirmingDelete]);

  const makeHandler = useCallback((fn?: (img: GalleryMedia) => void) => {
    return (e: React.MouseEvent) => {
      e.stopPropagation();
      haptic.impact('medium');
      if (image && fn) fn(image);
    };
  }, [image, haptic]);

  const handleRegenerate = makeHandler(onRegenerate);
  const handleUse = makeHandler(onUse);
  const handleAnimate = makeHandler(onAnimate);
  const handleUpscale = makeHandler(onUpscale);
  const handleEditImage = makeHandler(onEditImage);
  const handleUseVideo = makeHandler(onUseVideo);

  const handleThumbnailClick = useCallback((img: GalleryMedia) => {
    haptic.selection();
    videoPlayer.pauseAllVideos();
    resetZoom();
    onSelect?.(img);
  }, [onSelect, haptic, videoPlayer.pauseAllVideos, resetZoom]);

  const handleContentClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handleBackdropClick = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSlideChange = useCallback((swiper: SwiperType) => {
    if (isProgrammaticSlide.current) return;
    const newIndex = swiper.activeIndex;
    if (newIndex >= 0 && newIndex < images.length && images[newIndex].id !== image?.id) {
      videoPlayer.pauseAllVideos();
      resetZoom();
      onSelect?.(images[newIndex]);
      if (hasMoreItems && onLoadMore && newIndex >= images.length - 10) onLoadMore();
    }
  }, [images, image, onSelect, hasMoreItems, onLoadMore, videoPlayer.pauseAllVideos, resetZoom]);

  const handleInfoToggle = useCallback(() => {
    haptic.impact('light');
    if (showActionPanel) animateClose();
    else animateOpen();
  }, [haptic, showActionPanel, animateClose, animateOpen]);

  const handleMobileClose = useCallback(() => {
    haptic.impact('light');
    onClose();
  }, [haptic, onClose]);

  if (!image) return null;

  return (
    <div
      className={clsx(styles.backdrop, !isSidebarOpen && styles.fullscreen)}
      onClick={handleBackdropClick}
    >
      <div className={styles.container}>
        <div className={styles.content}>
          {/* MOBILE */}
          <MobileCarousel
            image={image}
            images={images}
            currentIndex={currentIndex}
            showActionPanel={showActionPanel}
            isVideo={isVideo}
            isSvg={isSvg}
            canRegenerate={canRegenerate}
            isDownloading={isDownloading}
            confirmingDelete={confirmingDelete}
            pullRef={pullRef}
            mobileCarouselRef={mobileCarouselRef}
            pinchContainerRef={pinchContainerRef}
            actionPanelRef={actionPanelRef}
            swiperRef={videoPlayer.swiperRef}
            mobileThumbnailsRef={mobileThumbnailsRef}
            pinchHandlers={pinchHandlers}
            onSlideChange={handleSlideChange}
            onThumbnailClick={handleThumbnailClick}
            onContentClick={handleContentClick}
            onRegenerate={onRegenerate ? handleRegenerate : undefined}
            onUse={onUse ? handleUse : undefined}
            onUpscale={onUpscale ? handleUpscale : undefined}
            onEditImage={onEditImage ? handleEditImage : undefined}
            onAnimate={onAnimate ? handleAnimate : undefined}
            onUseVideo={onUseVideo ? handleUseVideo : undefined}
            onDownload={handleDownload}
            onDelete={handleDelete}
            onUseReference={onUseReference}
            hideDownload={hideDownload}
            hideDelete={hideDelete}
            hideParameters={hideParameters}
            primaryAction={primaryAction}
          />

          {/* DESKTOP */}
          <DesktopView
            image={image}
            images={images}
            isVideo={isVideo}
            isSidebarOpen={isSidebarOpen}
            isZoomEnabled={isZoomEnabled}
            setIsZoomEnabled={setIsZoomEnabled}
            setIsSidebarOpen={setIsSidebarOpen}
            videoRef={videoPlayer.videoRef}
            isPlaying={videoPlayer.isPlaying}
            currentTime={videoPlayer.currentTime}
            duration={videoPlayer.duration}
            volume={videoPlayer.volume}
            isMuted={videoPlayer.isMuted}
            buffered={videoPlayer.buffered}
            showBigPlay={videoPlayer.showBigPlay}
            togglePlay={videoPlayer.togglePlay}
            handleVideoTimeUpdate={videoPlayer.handleVideoTimeUpdate}
            handleVideoLoadedMetadata={videoPlayer.handleVideoLoadedMetadata}
            handleVideoProgress={videoPlayer.handleVideoProgress}
            handleVideoPlay={videoPlayer.handleVideoPlay}
            handleVideoPause={videoPlayer.handleVideoPause}
            handleProgressClick={videoPlayer.handleProgressClick}
            handleVolumeChange={videoPlayer.handleVolumeChange}
            toggleMute={videoPlayer.toggleMute}
            toggleFullscreen={videoPlayer.toggleFullscreen}
            formatTime={videoPlayer.formatTime}
            imageWrapperRef={imageWrapperRef}
            handleMouseMove={handleMouseMove}
            handleMouseLeave={handleMouseLeave}
            handleImageClick={handleImageClick}
            thumbnailsRef={thumbnailsRef}
            onThumbnailClick={handleThumbnailClick}
            onContentClick={handleContentClick}
          />

          {/* SIDEBAR */}
          <DesktopSidebar
            image={image}
            isVideo={isVideo}
            isSvg={isSvg}
            isSidebarOpen={isSidebarOpen}
            canRegenerate={canRegenerate}
            isDownloading={isDownloading}
            confirmingDelete={confirmingDelete}
            onClose={onClose}
            onRegenerate={handleRegenerate}
            onUse={handleUse}
            onUpscale={handleUpscale}
            onEditImage={handleEditImage}
            onAnimate={handleAnimate}
            onUseVideo={handleUseVideo}
            onDownload={handleDownload}
            onDelete={handleDelete}
            onContentClick={handleContentClick}
            hasOnUse={!!onUse}
            hasOnUpscale={!!onUpscale}
            hasOnEditImage={!!onEditImage}
            hasOnAnimate={!!onAnimate}
            hasOnUseVideo={!!onUseVideo}
            onUseReference={onUseReference}
            hideDownload={hideDownload}
            hideDelete={hideDelete}
            hideParameters={hideParameters}
            primaryAction={primaryAction}
          />
        </div>
      </div>

      {/* MOBILE FLOATING CONTROLS */}
      <MobileControls
        showActionPanel={showActionPanel}
        isDownloading={isDownloading}
        onInfoToggle={handleInfoToggle}
        onDownload={handleDownload}
        onClose={handleMobileClose}
        onContentClick={handleContentClick}
        hideDownload={hideDownload}
        primaryAction={primaryAction}
      />
    </div>
  );
};

export default ImageModal;
